import moment from 'moment';

export enum PaymentTerms {
    Cash = 'Cash',
    Net15 = 'Net 15',
    Net30 = 'Net 30',
    Disc2in10Net30 = '-2% in 10 / Net 30',
    Net60 = 'Net 60',
    Disc2in10Net60 = '-2% in 10 / Net 60'
}

export enum PaymentMethod {
    Cash = 1,
    Check = 2,
    Card = 3
}

export enum CardBrand {
    Visa = 'VI',
    MasterCard = 'MC',
    Discover = 'DS',
    AmericanExpress = 'AE',
    PayPal = 'PP'
}

export class Payment implements IPayment {
    constructor(payment?: IPayment) {
        // initialize typed object from generic object
        this.paymentId = payment?.paymentId;
        this.paymentNumber = payment?.paymentNumber;
        this.method = payment?.method ?? PaymentMethod.Cash;
        this.amount = payment?.amount ?? 0;
        this.checkNumber = payment?.checkNumber;
        this.cardBrand = payment?.cardBrand;
        this.cardSuffix = payment?.cardSuffix;
        if (payment && payment.cardExpirationDate)
            this.cardExpirationDate = typeof (payment.cardExpirationDate) === "string" ? moment(payment?.cardExpirationDate).toDate() : payment.cardExpirationDate;
        this.cardTransactionCode = payment?.cardTransactionCode;
    }

    toJSON(): object {
        // return generic object with properties to stringify
        return {
            paymentId: this.paymentId || undefined,
            paymentNumber: this.paymentNumber || undefined,
            method: this.method !== PaymentMethod.Cash ? this.method : undefined,
            amount: this.amount || undefined,
            checkNumber: this.checkNumber || undefined,
            cardBrand: this.cardBrand || undefined,
            cardSuffix: this.cardSuffix || undefined,
            cardExpirationDate: this.cardExpirationDate || undefined,
            cardTransactionCode: this.cardTransactionCode || undefined
        };
    }

    paymentId?: string;
    paymentNumber?: string;
    method: PaymentMethod;
    amount: number;
    checkNumber?: string;
    cardBrand?: string;
    cardSuffix?: string;
    cardTransactionCode?: string;

    // Calculated properties

    private _cardExpirationDate?: Date;

    get cardExpirationDate(): Date|undefined {
        return this._cardExpirationDate;
    }

    set cardExpirationDate(value: Date|undefined) {
        this._cardExpirationDate = value ? new Date(value.getFullYear(), value.getMonth()) : undefined;
    }

    get cardExpirationMMYY(): string|undefined {
        if (this.method !== PaymentMethod.Card) return undefined;
        if (!this._cardExpirationDate) return "0000";
        const year = this._cardExpirationDate.getFullYear() % 100;
        const month = this._cardExpirationDate.getMonth() + 1;
        return `${month.toString().padStart(2, "0")}${year.toString().padStart(2, "0")}`;
    }

    set cardExpirationMMYY(value: string|undefined) {
        if (!value || value === "0000") this._cardExpirationDate = undefined;
        else try {
            const parts = /^(0[1-9]|1[0-2])([0-0][0-9])$/.exec(value);
            if (!parts) throw new Error("Expected 4 digits.");
            const month = parseInt(parts[1]) - 1;
            const year = parseInt(parts[2]) + 2000;
            this._cardExpirationDate = new Date(year, month);
        } catch (err) {
            throw new Error(`Card expiration "${value}" must be formatted as MMYY. ${err}`);
        }
    }

    toString(): string {
        switch (this.method) {
        case PaymentMethod.Cash:
            return `${this.paymentNumber ?? this.paymentId}: ${this.amount} (Cash)`;
        case PaymentMethod.Check:
            return `${this.paymentNumber ?? this.paymentId}: ${this.amount} (Check #${this.checkNumber})`;
        case PaymentMethod.Card:
            return `${this.paymentNumber ?? this.paymentId}: ${this.amount} (${this.cardBrand}-${this.cardSuffix} ${this.cardExpirationMMYY} REF#${this.cardTransactionCode}`;
        default:
            return "";
        }
    }
}

export interface IPayment {
    paymentId?: string;
    paymentNumber?: string;
    method?: PaymentMethod;
    amount?: number;
    checkNumber?: string;
    cardBrand?: string;
    cardSuffix?: string;
    cardExpirationDate?: Date;
    cardTransactionCode?: string;
}